import { IAppContext } from './../App/AppContext';
import { IColumn } from '@fluentui/react';
import { LocalStorageKeys, getLocalStorageData, setLocalStorageData } from './localstorage';
import Logger from 'services/Logging/logService';

/**
 * The stored columns of a list with the column chooser (PLAN-CTI-089), one JSON object under the list's <List>Columns
 * key: `order` holds every column key as last saved, the pinned ones first; `tokens` the hidden-column tokens of
 * isColumnVisible (a key hides a column, "!key" shows a column that is hidden by default). A value stored before the
 * order existed is a comma-separated token list: it reads as those tokens and no order, so nobody loses a choice.
 */
export interface ColumnState {
  order: string[];
  tokens: string[];
}

const toStrings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

export const readColumnState = (appContext: IAppContext, cacheKey: LocalStorageKeys): ColumnState => {
  try {
    const storedData = getLocalStorageData(appContext, cacheKey);
    if (!storedData) return { order: [], tokens: [] };

    if (storedData.startsWith('{')) {
      const parsed = JSON.parse(storedData) as Partial<ColumnState>;

      return { order: toStrings(parsed.order), tokens: toStrings(parsed.tokens) };
    }

    return { order: [], tokens: storedData.split(',') };
  } catch (err) {
    Logger.debug('readColumnState', err);
  }

  return { order: [], tokens: [] };
};

export const writeColumnState = (appContext: IAppContext, cacheKey: LocalStorageKeys, state: ColumnState) => {
  setLocalStorageData(appContext, cacheKey, JSON.stringify(state));
};

export const isColumnVisible = (col: IColumn, tokens: string[]): boolean => {
  if (col.data?.defaultHidden === true) {
    return tokens.some((token) => token === '!' + col.key);
  }

  return !tokens.some((token) => token === col.key);
};

export const buildColumnTokens = (columns: IColumn[], checked: boolean[]): string[] => {
  return columns.reduce<string[]>((acc, col, index) => {
    if (col.data?.defaultHidden === true) {
      if (checked[index] === true) {
        acc.push('!' + col.key);
      }
    } else if (checked[index] !== true) {
      acc.push(col.key);
    }

    return acc;
  }, []);
};

/**
 * The columns in the stored order: the first `pinnedCount` in code order (they cannot be moved), then the keys of
 * `order` that still exist, then the columns the order does not know (added to the code, or a criteria column that
 * appeared later) in code order. A key of `order` without a column is passed over.
 */
export const applyColumnOrder = (columns: IColumn[], order: string[], pinnedCount: number = 1): IColumn[] => {
  const pinned = columns.slice(0, pinnedCount);
  const rest = columns.slice(pinnedCount);
  const unplaced = new Map(rest.map((col) => [col.key, col]));
  const ordered: IColumn[] = [];

  for (const key of order) {
    const col = unplaced.get(key);
    if (col) {
      ordered.push(col);
      unplaced.delete(key);
    }
  }

  return [...pinned, ...ordered, ...rest.filter((col) => unplaced.has(col.key))];
};

// The visible columns of a list in the stored order; `pinnedCount` is the chooser's blockFirstCount (default 1).
export const filterColumnsFromLocalStorage = (
  appContext: IAppContext,
  cacheKey: LocalStorageKeys,
  columns: IColumn[],
  pinnedCount: number = 1,
): IColumn[] => {
  const state = readColumnState(appContext, cacheKey);

  return applyColumnOrder(columns, state.order, pinnedCount).filter((col) => isColumnVisible(col, state.tokens));
};
